import React from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useRecoilValue } from 'recoil';

import {
  currentWeatherConditionsPageTestIDs,
  currentWeatherConditionsTestIDs,
  myWeatherPageTestIDs,
  Theme,
} from '@constants';
import { useSegmentTrackWeatherForecast, useWeatherAlerts } from '@hooks';
import { navigationService } from '@services';
import { currentLocationWeatherState } from '@store';

import { alertsHeadlinesType } from '../../../constants/types/api/alertsHeadlinesType';
import { currentWeatherConditionsType } from '../../../constants/types/api/currentWeatherConditionsType';
import { isDisplayable, missingValue } from '../../../helpers/StringUtils';
import { TWCTile } from '../../focusable/TWCTile/TWCTile';
import { TWCIcon } from '../../primitives/TWCIcon/TWCIcon';
import { TWCText } from '../../primitives/TWCText/TWCText';

const CurrentWeatherConditionsTileAPI = ({ nextFocusDown, nextFocusLeft, nextFocusRight, nextFocusUp }) => {
  const currentLocationWeatherResponse = useRecoilValue(currentLocationWeatherState);
  const alerts = useWeatherAlerts();

  return (
    <CurrentWeatherConditionsTileUI
      alerts={alerts}
      currentLocationWeatherResponse={currentLocationWeatherResponse}
      nextFocusDown={nextFocusDown}
      nextFocusLeft={nextFocusLeft}
      nextFocusRight={nextFocusRight}
      nextFocusUp={nextFocusUp}
    />
  );
};

const tPath = 'currentWeatherConditionsTile';

const CurrentWeatherConditionsTileUI = ({
  alerts,
  currentLocationWeatherResponse,
  nextFocusDown,
  nextFocusLeft,
  nextFocusRight,
  nextFocusUp,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: tPath,
  });
  const { data, isLoading } = currentLocationWeatherResponse;
  let { error } = currentLocationWeatherResponse;
  /**
   * Alerts Headlines have a binary state; either there are headlines, or there are not
   * When headlines are loading, or if there is an error in response, treat it the same as if there are no headlines
   */
  const hasAlertsHeadlines = alerts?.length > 0;

  const [forecastType, setSegmentTrackWeatherForecast] = useSegmentTrackWeatherForecast();

  if (!data) {
    error = 'Current weather data failed to parse.';
  }

  const handleOnPress = () => {
    setSegmentTrackWeatherForecast(forecastType.NOW);
    navigationService.navigate(currentWeatherConditionsPageTestIDs.currentWeatherConditionsPage);
  };

  const getValid = value => (isDisplayable(value, null) ? value : 'unknown');

  return (
    <TWCTile
      accessibilityHint={t('tileAccessibilityHint')}
      accessibilityLabel={
        error
          ? t('errorAccessibilityLabel')
          : `${t('tileAccessibilityLabel', {
              weather: getValid(data.wxPhraseLong),
              temperature: getValid(data.temperature),
              feelsLike: getValid(data.temperatureFeelsLike),
              windDirection: getValid(data.windDirectionCardinal),
              windSpeed: getValid(data.windSpeed),
            })}${hasAlertsHeadlines ? t('tileAlertAccessibilityLabel', { count: alerts.length }) : ''}`
      }
      height={198}
      nextFocusDown={nextFocusDown}
      nextFocusLeft={nextFocusLeft}
      nextFocusRight={nextFocusRight}
      nextFocusUp={nextFocusUp}
      testID={myWeatherPageTestIDs.currentWeatherConditionsTile}
      width={432}
      onPress={handleOnPress}
    >
      <View style={[styles.headerWrapper, hasAlertsHeadlines ? { backgroundColor: Theme.alertRed } : null]}>
        <TWCText testID={currentWeatherConditionsTestIDs.headerText} variant={'regularText24Transparent'}>
          {t('headerText')}
        </TWCText>
        {hasAlertsHeadlines ? (
          <TWCText testID={currentWeatherConditionsTestIDs.alertsHeadlinesText} variant={'tileHeader'}>
            {t('alertsHeadlinesText', { count: alerts.length })}
          </TWCText>
        ) : null}
      </View>
      <View style={styles.contentWrapper}>
        {isLoading ? (
          <ActivityIndicator color={Theme.loading} size={'large'} />
        ) : (
          <>
            <View style={styles.leftContentWrapper}>
              {!data || error ? (
                <TWCText noTestID variant={'mediumText54'}>
                  {missingValue}
                </TWCText>
              ) : (
                <>
                  <TWCIcon code={data.iconCodeExtend} height={90} type={'current'} width={90} />
                  <TWCText
                    style={styles.temperatureText}
                    testID={currentWeatherConditionsTestIDs.temperatureText}
                    variant={'mediumText54'}
                  >
                    {isDisplayable(data.temperature, error) ? `${data.temperature}°` : missingValue}
                  </TWCText>
                </>
              )}
            </View>
            <View style={styles.rightContentWrapper}>
              <TWCText testID={currentWeatherConditionsTestIDs.feelsLikeText} variant={'regularText24Transparent'}>
                {t('feelsLikeText')}
              </TWCText>
              {!data || error ? (
                <TWCText noTestID variant={'mediumText30'}>
                  {missingValue}
                </TWCText>
              ) : (
                <TWCText testID={currentWeatherConditionsTestIDs.feelsLikeTemperatureText} variant={'regularText30'}>
                  {isDisplayable(data.temperatureFeelsLike, error) ? `${data.temperatureFeelsLike}°` : missingValue}
                </TWCText>
              )}
              <TWCText testID={currentWeatherConditionsTestIDs.windText} variant={'regularText24Transparent'}>
                {t('windText')}
              </TWCText>
              {!data || error ? (
                <TWCText noTestID variant={'mediumText30'}>
                  {missingValue}
                </TWCText>
              ) : (
                <TWCText testID={currentWeatherConditionsTestIDs.windContentText} variant={'regularText30'}>{`${
                  data.windDirectionCardinal
                } ${data.windSpeed} ${t('windUnitsText')}`}</TWCText>
              )}
            </View>
          </>
        )}
      </View>
    </TWCTile>
  );
};

export const CurrentWeatherConditionsTile = {
  API: CurrentWeatherConditionsTileAPI,
  UI: CurrentWeatherConditionsTileUI,
};

CurrentWeatherConditionsTileUI.propTypes = {
  alerts: alertsHeadlinesType,
  currentLocationWeatherResponse: PropTypes.shape({
    data: currentWeatherConditionsType,
    error: PropTypes.any,
    isLoading: PropTypes.bool,
  }),
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
};

CurrentWeatherConditionsTileUI.defaultProps = {
  alerts: null,
  currentLocationWeatherResponse: null,
  nextFocusDown: null,
  nextFocusLeft: null,
  nextFocusRight: null,
  nextFocusUp: null,
};

const styles = StyleSheet.create({
  contentWrapper: {
    backgroundColor: Theme.withOpacity(Theme.blackPearlBlue, 0.5),
    borderBottomLeftRadius: Theme.tileBorderRadius,
    borderBottomRightRadius: Theme.tileBorderRadius,
    elevation: 500,
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  headerWrapper: {
    borderTopLeftRadius: Theme.tileBorderRadius,
    borderTopRightRadius: Theme.tileBorderRadius,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: Theme.px(30),
    paddingVertical: Theme.px(7),
  },
  leftContentWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    flex: 0.5,
    justifyContent: 'center',
  },
  rightContentWrapper: {
    alignItems: 'flex-end',
    flex: 0.5,
    justifyContent: 'center',
    paddingRight: Theme.px(30),
  },
  temperatureText: {
    paddingLeft: Theme.px(6),
  },
});
